import { useGetIdentity, useRecordContext } from "ra-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Languages, LoaderCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Contact } from "../types";
import {
  fetchContactLanguage,
  setContactLanguage,
} from "../contact-language/client";
import {
  ContactLanguageRequestError,
  type ContactLanguageState,
  type SupportedLanguage,
} from "../contact-language/contracts";
import {
  DEFAULT_LANGUAGE,
  VENDORED_LANGUAGES,
  formatLanguage,
  languageLabel,
  languageSourceLabel,
} from "../contact-language/languages";
import { useIsAgencyMember } from "./useAgencyMembership";

type LanguageChange = {
  whatsappId: string;
  code: string | null;
  expectedUpdatedAt: string | null;
};

function saveErrorMessage(error: unknown) {
  if (error instanceof ContactLanguageRequestError) {
    switch (error.code) {
      case "conflict":
        return "The language changed elsewhere (the contact or Felix). Refresh the status, then try again.";
      case "unsupported_language":
        return "The bot does not enable that language. Refresh the status to reload the list.";
      case "unknown_contact":
        return "The bot has no record of this contact yet — they must message Felix once before a language can be set.";
      case "unauthorized":
        return "Your session expired. Sign in again to change the language.";
      case "forbidden":
        return "Administrator access is required to change the language.";
      case "unconfigured":
        return "The contact-language proxy is not configured on this deployment (TEST_ECHO_BOT_BASE_URL / INTERNAL_OPS_API_KEY).";
      default:
        if (error.status >= 400 && error.status < 500) {
          const codes = [error.message, error.details.reason].filter(
            (value): value is string =>
              typeof value === "string" &&
              /^[a-z][a-z0-9_]{0,119}$/.test(value),
          );
          return `Language change was rejected${
            codes.length ? ` (${codes.join(": ")})` : ""
          }. Refresh the status before trying again.`;
        }
    }
  }
  return "Could not confirm whether the language change was saved. Refresh the status before trying again.";
}

/** Bot-advertised list, or the vendored registry copy when the bot sent none. */
function pickableLanguages(state: ContactLanguageState): SupportedLanguage[] {
  const source = state.supported_languages.length
    ? state.supported_languages
    : VENDORED_LANGUAGES;
  return source.filter((language) => language.code !== DEFAULT_LANGUAGE);
}

function formatUpdatedAt(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Per-contact "Felix speaks your language" control (plan 2026-09-21 §4.5 /
 * LANG-500). Same shape as ContactPaywallToggle: the bot's record is the
 * source of truth, every save carries the `updated_at` it was based on so a
 * stale CRM tab cannot overwrite a choice the contact just confirmed on
 * WhatsApp, and a failed save keeps showing the last confirmed value.
 *
 * Writes are administrator-only (the proxy enforces it; the identity flag only
 * disables the controls up front), reads are open to every CRM member.
 *
 * On a real-estate agency's WhatsApp number the preference only changes the
 * B2C chat: real-estate films keep the agency's Studio language and never
 * gain a B2C-only language (plan 2026-10-10 §5.9), so the section says so.
 */
export const ContactLanguageSelector = () => {
  const contact = useRecordContext<Contact>();
  const whatsappId = contact?.whatsapp_user_id;
  const { identity } = useGetIdentity();
  const canEdit = identity?.administrator === true;
  const queryClient = useQueryClient();
  const realEstateAgent = useIsAgencyMember(whatsappId);
  const queryKey = ["contact-language", whatsappId];
  const query = useQuery({
    queryKey,
    queryFn: () => fetchContactLanguage(whatsappId!),
    enabled: Boolean(whatsappId),
    retry: false,
    staleTime: 30_000,
  });
  const mutation = useMutation({
    onMutate: ({ whatsappId }: LanguageChange) =>
      queryClient.cancelQueries({
        queryKey: ["contact-language", whatsappId],
      }),
    mutationFn: ({ whatsappId, code, expectedUpdatedAt }: LanguageChange) =>
      setContactLanguage(whatsappId, code, expectedUpdatedAt),
    onSuccess: (state, { whatsappId }) =>
      queryClient.setQueryData(["contact-language", whatsappId], state),
    onError: (_error, { whatsappId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["contact-language", whatsappId],
      });
    },
  });

  if (!whatsappId)
    return (
      <p className="text-xs text-muted-foreground">
        Language requires a linked WhatsApp contact.
      </p>
    );
  if (!query.data) {
    const unconfigured =
      query.error instanceof ContactLanguageRequestError &&
      query.error.code === "unconfigured";
    return (
      <div className="text-xs text-muted-foreground">
        {query.isPending
          ? "Loading language preference…"
          : unconfigured
            ? "Language preference unavailable: the contact-language proxy is not configured on this deployment."
            : "Language preference unavailable."}
        {query.isError && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  const state = query.data;
  const policyOff = state.policy_mode === "off";
  const saveFailed =
    mutation.isError && mutation.variables?.whatsappId === whatsappId;
  const controlsDisabled =
    !canEdit ||
    policyOff ||
    mutation.isPending ||
    query.isError ||
    (saveFailed && query.isFetching);
  const current = state.preferred_language
    ? languageLabel(state.preferred_language, state.supported_languages)
    : null;
  const sourceLabel = current ? languageSourceLabel(state.source) : null;
  const updatedAt = current ? formatUpdatedAt(state.updated_at) : null;
  const changeLanguage = (code: string | null) =>
    mutation.mutate({
      whatsappId,
      code,
      expectedUpdatedAt: state.updated_at,
    });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {mutation.isPending ? (
          <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
        ) : (
          <Languages className="size-4 text-muted-foreground" />
        )}
        <span className="text-xs" role="status">
          {query.isError ? "Last confirmed: " : "Current: "}
          {current
            ? `${formatLanguage(current)} for this contact`
            : "English (default)"}
        </span>
        {sourceLabel ? (
          <Badge variant="outline" className="text-xs">
            {sourceLabel}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Preferred language"
          className="h-8 rounded-md border border-input bg-background px-2 text-xs shadow-xs disabled:cursor-not-allowed disabled:opacity-50"
          value={state.preferred_language ?? ""}
          disabled={controlsDisabled}
          onChange={(event) => changeLanguage(event.target.value || null)}
        >
          <option value="">English (default)</option>
          {pickableLanguages(state).map((language) => (
            <option key={language.code} value={language.code}>
              {formatLanguage(language)}
            </option>
          ))}
        </select>
        {state.preferred_language !== null && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            disabled={controlsDisabled}
            onClick={() => changeLanguage(null)}
          >
            Use default (English)
          </Button>
        )}
      </div>
      {updatedAt ? (
        <p className="text-xs text-muted-foreground">Updated {updatedAt}</p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {policyOff
          ? "Language feature is off in this environment."
          : state.policy_mode === "observe"
            ? "Observe mode: the preference is stored, but Felix and the next Echo stay in English until the policy is active."
            : "Applies to the next conversation turn and the next B2C Echo. Existing Echoes keep their language."}
      </p>
      {realEstateAgent && (
        <p className="text-xs text-muted-foreground">
          B2C chat only — real-estate films stay in the agency's Studio
          language.
        </p>
      )}
      {!canEdit && !policyOff && (
        <p className="text-xs text-muted-foreground">
          Administrator access is required to change the language.
        </p>
      )}
      {(saveFailed || query.isError) && (
        <div className="space-y-1">
          <p role="alert" className="text-xs text-destructive">
            {query.isError
              ? "Could not refresh the language preference. The value shown is the last confirmed one."
              : saveErrorMessage(mutation.error)}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={query.isFetching}
            onClick={async () => {
              const result = await query.refetch();
              if (result.isSuccess) mutation.reset();
            }}
          >
            Refresh status
          </Button>
        </div>
      )}
    </div>
  );
};
